import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import authEndpoints from 'config/api/auth';
import PATHS from 'router/PATHS';
import { clearAuthorizationHeader, setAuthorizationHeader } from 'services/authHeaders/authHeaders';
import accessTokenStorage from 'storages/accessTokenStorage';
import refreshTokenStorage from 'storages/refreshTokenStorage';
import userDataStorage from 'storages/userDataStorage';

import { AuthRefreshTransientError, createAuthRefreshCoordinator, isSimpleJwtTerminalRefreshFailure } from './authRefreshCoordinator';

const COORDINATION_STORAGE_KEY = 'pocketsafe_auth_refresh_coordination_v1';
const LOCK_NAME = 'pocketsafe-auth-refresh-v1';
const CHANNEL_NAME = 'pocketsafe-auth-state-v1';
const LOCK_WAIT_MS = 12000;
const REFRESH_TIMEOUT_MS = 10000;
const LEASE_MS = 15000;

const defaultState = {
  fence: 0,
  leaseExpiresAt: 0,
  ownerId: null,
  status: 'idle',
  version: 0,
};

const readCoordinationState = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(COORDINATION_STORAGE_KEY));
    if (!parsed || !Number.isFinite(parsed.fence) || !Number.isFinite(parsed.version)) return { ...defaultState };
    return {
      fence: parsed.fence,
      leaseExpiresAt: Number.isFinite(parsed.leaseExpiresAt) ? parsed.leaseExpiresAt : 0,
      ownerId: typeof parsed.ownerId === 'string' ? parsed.ownerId : null,
      status: ['idle', 'refreshing', 'updated', 'cleared'].includes(parsed.status) ? parsed.status : 'idle',
      version: parsed.version,
    };
  } catch {
    return { ...defaultState };
  }
};

const writeCoordinationState = state => {
  window.localStorage.setItem(COORDINATION_STORAGE_KEY, JSON.stringify(state));
};

const runWithBrowserLock = callback => {
  if (!window.navigator?.locks?.request) {
    return Promise.reject(new AuthRefreshTransientError('This browser does not provide the required Web Locks API.'));
  }

  const abortController = new AbortController();
  const timeout = window.setTimeout(() => abortController.abort(), LOCK_WAIT_MS);
  return window.navigator.locks
    .request(LOCK_NAME, { mode: 'exclusive', signal: abortController.signal }, callback)
    .finally(() => window.clearTimeout(timeout));
};

const refreshHttpClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: REFRESH_TIMEOUT_MS,
});

let channel;
let synchronizationInitialized = false;

const coordinator = createAuthRefreshCoordinator({
  authStorage: {
    clear: () => {
      refreshTokenStorage.destroy();
      accessTokenStorage.destroy();
      userDataStorage.destroy();
      clearAuthorizationHeader();
    },
    getAccess: accessTokenStorage.get,
    getRefresh: refreshTokenStorage.get,
    store: ({ access, refresh }) => {
      // Store the rotated refresh credential first. If the tab is terminated
      // between writes, the recoverable credential is not lost.
      refreshTokenStorage.set(refresh);
      accessTokenStorage.set(access);
    },
  },
  coordinationStorage: {
    read: readCoordinationState,
    write: writeCoordinationState,
  },
  isTerminalRefreshFailure: isSimpleJwtTerminalRefreshFailure,
  leaseMs: LEASE_MS,
  lock: { runExclusive: runWithBrowserLock },
  now: () => Date.now(),
  ownerId: uuidv4(),
  publish: signal => {
    if (channel) channel.postMessage(signal);
  },
  redirectToLogin: () => {
    window.location.href = PATHS.AUTH;
  },
  refreshRequest: refresh =>
    refreshHttpClient({
      ...authEndpoints.refresh(),
      data: { refresh },
      headers: { Authorization: undefined },
    }).then(({ data }) => data),
  setAuthorization: access => setAuthorizationHeader(access),
});

const applyStoredCoordinationSignal = () => {
  const state = readCoordinationState();
  coordinator.applyExternalSignal({ status: state.status, version: state.version });
};

export const initializeAuthRefreshSynchronization = () => {
  if (synchronizationInitialized) return;
  synchronizationInitialized = true;

  if (typeof window.BroadcastChannel === 'function') {
    channel = new window.BroadcastChannel(CHANNEL_NAME);
    channel.addEventListener('message', event => coordinator.applyExternalSignal(event.data));
  }

  // The coordination record contains metadata only, so the storage-event
  // fallback never propagates access or refresh credential values.
  window.addEventListener('storage', event => {
    if (event.key === COORDINATION_STORAGE_KEY) applyStoredCoordinationSignal();
  });
};

export const refreshAuthTokens = () => coordinator.refresh();
